// MacFamilyTree (.mftpkg) → Tree.
//
// A .mftpkg is a folder: `database` is a Core Data SQLite store, `resources/pictures/` holds
// photos, and `metadata` is a property list of counts and settings. Core Data puts every
// subclass of BaseObject (Person, Family, PersonEvent, Place, Note, media…) in one table,
// ZBASEOBJECT, told apart by Z_ENT. Entity numbers and some column suffixes (ZPERSON1 vs
// ZPERSON2) depend on the model version, so entities are looked up by name in Z_PRIMARYKEY
// and suffixed columns are read with pick(), which takes whichever variant is filled in.
//
// Families become partner and parent links the same way GEDCOM FAMs do (gedcom-import.ts).
// Religious events are kept in passthrough for GEDCOM export but not shown, as with GEDCOM.

import { MONTHS, parseUserDate } from '../model/dates';
import { markDeceasedFromDates, newId } from '../model/people';
import { placeKey } from '../model/places';
import { newTree } from '../model/tree';
import { fileKey, mimeFromName, type FileBag } from '../model/files';
import type {
  Citation, CitationQuality, EventType, Gender, Id, LifeEvent, MediaItem, MediaKind, NameType, ParentType, PartnerRel, Person, PersonName,
  PlaceLocation, RawRecord, Region, Relationship, Source, Todo, TodoPriority, TodoStatus, Tree,
} from '../model/types';
import { EVENT_TAGS, RELIGIOUS_TAGS, type ImportSummary } from './gedcom-import';
import { rec } from './gedcom-lines';
import { SqliteDb, type SqlRow, type SqlValue } from './sqlite';

/** The files inside a package, by path relative to the package folder ("database", "resources/pictures/…"). */
export interface PackageFiles {
  /** The package's name without ".mftpkg". */
  name: string;
  read(path: string): Promise<Uint8Array | undefined>;
}

export interface PackagePhoto {
  personId: Id;
  /** Key of the picture in `files`. */
  key: string;
  /** Where the person is in the picture, when it shows several people. */
  region?: Region;
}

export interface MacFamilyTreeResult {
  tree: Tree;
  summary: ImportSummary;
  /** Each person's main picture; the UI crops and shrinks them into Person.photo. */
  photos: PackagePhoto[];
  /** Media files by key (MediaItem.file.key). */
  files: FileBag;
  /** Separate preview images for media that aren't pictures (PDFs, videos), by media id. */
  thumbs: Map<Id, Blob>;
}

// Enum values, in the order MacFamilyTree lists them (checked against real files where the
// sample had them: name types 0, 3, 7; gender 0, 1; child relation 0).
const GENDERS: Gender[] = [{ identity: 'man' }, { identity: 'woman' }, { identity: 'unknown' }];
const CHILD_TYPES: ParentType[] = ['biological', 'adoptive', 'step', 'foster'];
// Name at birth, stage/pen name, other, married, double-barrelled, family name, variation,
// nickname, adoptive, formal, religious, title.
const NAME_TYPES: (NameType | 'nickname')[] = ['birth', 'alias', 'other', 'married', 'other', 'other', 'alias', 'nickname', 'other', 'other', 'alias', 'other'];
// To-dos: priority low, normal, high; status open, waiting, in progress, done; and the four task types.
const TODO_PRIORITIES: TodoPriority[] = ['low', 'normal', 'high'];
const TODO_STATUSES: TodoStatus[] = ['open', 'waiting', 'in-progress', 'done'];
const TODO_KINDS = ['Further research', 'Verify', 'Missing events', 'Cleanup'];
const MEDIA_KINDS: [string, MediaKind, string][] = [
  ['MediaPicture', 'image', 'ZPICTUREFILEIDENTIFIER'],
  ['MediaPDF', 'pdf', 'ZPDFFILEIDENTIFIER'],
  ['MediaAudio', 'audio', 'ZAUDIOFILEIDENTIFIER'],
  ['MediaVideo', 'video', 'ZVIDEOFILEIDENTIFIER'],
  ['MediaURL', 'link', 'ZURL'],
];
const MEDIA_NOUN: Record<MediaKind, string> = { image: 'Photo', pdf: 'Document', audio: 'Recording', video: 'Video', link: 'Link', file: 'File' };
const ASSOCIATE_LABELS = ['Godparent', 'Witness of birth', 'Witness of death', 'Witness of marriage', 'Witness of civil marriage', 'Witness of marriage'];

// MacFamilyTree's own tags that aren't in EVENT_TAGS.
const MFT_EVENT_TAGS: Record<string, EventType> = { MISE: 'military' };
// LDS and church events beyond the GEDCOM set: initiatory, mission, excommunication, circumcision.
const MFT_RELIGIOUS_TAGS = new Set(['WAC', 'MISS', 'EXCO', 'CIRC']);
// Identification numbers: kept for GEDCOM export like any unmodeled GEDCOM fact, never shown.
const PRIVATE_FACT_TAGS = new Set(['SSN', 'IDNO']);

const MARRIAGES = ['MARR', 'MRCI', 'MRRE'];
const MARRIAGE_PAPERWORK = ['MARC', 'MARL', 'MARB', 'MARS'];
const DIVORCES = ['DIV', 'ANUL', 'DIVF'];

const LABEL_OVERRIDES: Record<string, string> = {
  GenericEvent: 'Event',
  OtherEvent: 'Event',
  NobilityTypeTitle: 'Nobility title',
  NickName: 'Nickname',
  Divorce_Filing: 'Divorce filing',
  NationalID: 'National ID',
  LDSInitiatory: 'LDS initiatory',
};

interface ConclusionType {
  tag: string;
  label: string;
}

/** "_PersonFact_EyeColor" → "Eye color". User-created types carry their own name. */
function typeLabel(row: SqlRow): string {
  const own = str(row.ZTYPENAME);
  if (own && row.ZISUSERCREATED) return own;
  const key = (str(row.ZIDENTIFIER) ?? str(row.ZTYPENAMELOCALIZATIONKEY) ?? '').replace(/^_(Person|Family)(Event|Fact)_/, '');
  if (LABEL_OVERRIDES[key]) return LABEL_OVERRIDES[key];
  const words = key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim();
  return words ? words.charAt(0) + words.slice(1).toLowerCase() : own || 'Event';
}

function str(v: SqlValue | undefined): string | undefined {
  if (typeof v === 'number') return String(v);
  if (typeof v !== 'string') return undefined;
  const t = v.trim();
  return t || undefined;
}

function num(v: SqlValue | undefined): number | undefined {
  return typeof v === 'number' ? v : undefined;
}

/** The first filled-in variant of a column (ZPERSON, ZPERSON1, ZPERSON2…). */
function pick(row: SqlRow, base: string): SqlValue {
  for (const key of [base, base + '1', base + '2', base + '3', base + '4']) {
    const v = row[key];
    if (v !== null && v !== undefined && v !== '') return v;
  }
  return null;
}

/**
 * MacFamilyTree stores dates as text, as they were typed: day-first numbers ("27.11.1957",
 * "11.2003"), slashes in the order of the Mac's region ("11/27/1957" in the US, "27/11/1957" in
 * the UK; see slashDayFirst), GEDCOM-style words ("JUL 1915", "ABT 1960", "BET 9 May 1832 AND
 * 3 March 1835"), or English ("about 1783").
 */
export function mftDate(raw: string | undefined, dayFirst = false): string | undefined {
  const text = raw?.trim();
  if (!text) return undefined;
  const mon = (m: string) => MONTHS[Number(m) - 1] ?? m;
  const dated = (all: string, d: string, m: string, y: string) => (Number(m) >= 1 && Number(m) <= 12 ? `${Number(d)} ${mon(m)} ${y}` : all);
  const words = text
    .replace(/\b(\d{1,2})\.(\d{1,2})\.(\d{1,4})\b/g, dated)
    .replace(/\b(\d{1,2})\/(\d{1,2})\/(\d{3,4})\b/g, (all, a, b, y) => (dayFirst ? dated(all, a, b, y) : dated(all, b, a, y)))
    .replace(/\b(\d{1,2})\.(\d{3,4})\b/g, (all, m, y) => (Number(m) >= 1 && Number(m) <= 12 ? `${mon(m)} ${y}` : all));
  const parsed = parseUserDate(words);
  return parsed.understood ? parsed.value : `(${text.replace(/[()]/g, '')})`;
}

/**
 * A face rectangle, "{{x, y}, {w, h}}" as fractions of the picture with Cocoa's origin at the
 * bottom left, as a Region measured from the top left.
 */
export function mftRegion(text: string | undefined): Region | undefined {
  const n = text?.match(/-?[\d.]+(?:e-?\d+)?/gi)?.map(Number);
  if (!n || n.length !== 4 || n.some((x) => !Number.isFinite(x))) return undefined;
  const [x, y, w, h] = n;
  if (w <= 0 || h <= 0) return undefined;
  const clamp = (v: number) => Math.min(1, Math.max(0, v));
  return { x: clamp(x), y: clamp(1 - y - h), w: Math.min(w, 1), h: Math.min(h, 1) };
}

/**
 * Whether a file's slash dates put the day first, as a Mac set to the UK or most of Europe types
 * them: some date has a first number over 12 ("27/11/1957") and none a second one ("11/27/1957").
 * Otherwise they're read month first, as in the US.
 */
export function slashDayFirst(texts: Iterable<string>): boolean {
  let dayFirst = false;
  for (const t of texts)
    for (const m of t.matchAll(/\b(\d{1,2})\/(\d{1,2})\/\d{3,4}\b/g)) {
      if (Number(m[2]) > 12) return false;
      if (Number(m[1]) > 12) dayFirst = true;
    }
  return dayFirst;
}

/** A MacFamilyTree date as YYYY-MM-DD, when it names one exact day. */
function isoDay(raw: string | undefined, dayFirst = false): string | undefined {
  const m = mftDate(raw, dayFirst)?.match(/^(\d{1,2}) ([A-Z]{3}) (\d{4})$/);
  if (!m) return undefined;
  return `${m[3]}-${String(MONTHS.indexOf(m[2]) + 1).padStart(2, '0')}-${m[1].padStart(2, '0')}`;
}

function plural(n: number, one: string, many = one + 's'): string {
  return `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;
}

export async function importMacFamilyTree(pkg: PackageFiles): Promise<MacFamilyTreeResult> {
  const bytes = await pkg.read('database');
  if (!bytes) throw new Error('This MacFamilyTree package has no database inside it.');
  const db = new SqliteDb(bytes, await pkg.read('database-wal'));
  if (!db.hasTable('ZBASEOBJECT') || !db.hasTable('Z_PRIMARYKEY'))
    throw new Error("This database isn't from MacFamilyTree (it has no family tree tables).");
  const warnings: string[] = [];

  // ---- Entities by name, including subentities (MediaPicture is a BaseMedia).
  const entities = new Map<number, { name: string; parent: number }>();
  for (const r of db.rows('Z_PRIMARYKEY')) entities.set(num(r.Z_ENT) ?? -1, { name: str(r.Z_NAME) ?? '', parent: num(r.Z_SUPER) ?? 0 });
  const isA = (ent: SqlValue, name: string): boolean => {
    let e = num(ent);
    for (let guard = 0; e && guard < 20; guard++) {
      const info = entities.get(e);
      if (!info) return false;
      if (info.name === name) return true;
      e = info.parent;
    }
    return false;
  };

  const objects = db.rows('ZBASEOBJECT');
  const byPk = new Map<number, SqlRow>();
  for (const r of objects) byPk.set(num(r.Z_PK)!, r);
  const of = (name: string) => objects.filter((r) => isA(r.Z_ENT, name));
  // Which way round this file's slash dates go, judged from all of them; every date is read the same way.
  const dateColumns = ['ZDATE', 'ZDATE1', 'ZDATE2', 'ZDATE3', 'ZDATE4', 'ZDUEDATE'];
  const dayFirst = slashDayFirst(objects.flatMap((r) => dateColumns.map((k) => str(r[k]) ?? '')));
  const readDate = (raw: string | undefined) => mftDate(raw, dayFirst);

  const conclusionTypes = new Map<number, ConclusionType>();
  for (const r of db.rows('ZCONCLUSIONTYPE')) conclusionTypes.set(num(r.Z_PK)!, { tag: (str(r.ZGEDCOMTAG) ?? '').toUpperCase(), label: typeLabel(r) });

  const places = new Map<number, string>();
  for (const r of of('Place')) {
    const parts: string[] = [];
    for (const v of [pick(r, 'ZPLACE'), r.ZCOUNTY, r.ZSTATE, r.ZCOUNTRY]) {
      const s = str(v);
      if (s && !parts.some((p) => p.toLowerCase() === s.toLowerCase())) parts.push(s);
    }
    if (!parts.length && str(r.ZNAME)) parts.push(str(r.ZNAME)!);
    if (parts.length) places.set(num(r.Z_PK)!, parts.join(', '));
  }

  // Where places are: degrees, minutes, and seconds, with flags for south and west.
  const locations: Record<string, PlaceLocation> = {};
  if (db.hasTable('ZCOORDINATE'))
    for (const r of db.rows('ZCOORDINATE')) {
      const place = places.get(num(r.ZPLACE) ?? -1);
      const lat = degrees(r.ZLATITUDEDEGREES, r.ZLATITUDEMINUTES, r.ZLATITUDESECONDS, r.ZLATITUDEISSOUTH);
      const lon = degrees(r.ZLONGITUDEDEGREES, r.ZLONGITUDEMINUTES, r.ZLONGITUDESECONDS, r.ZLONGITUDEISWEST);
      if (place && lat !== undefined && lon !== undefined && Math.abs(lat) <= 90 && Math.abs(lon) <= 180 && (lat || lon)) locations[placeKey(place)] = { lat, lon };
    }

  // Notes hang off any NoteContainer (person, family, event…) through ZOBJECT.
  const notesFor = new Map<number, string[]>();
  for (const r of of('Note')) {
    const owner = num(r.ZOBJECT);
    const text = str(pick(r, 'ZTEXT'));
    if (owner === undefined || !text) continue;
    notesFor.set(owner, [...(notesFor.get(owner) ?? []), text]);
  }
  const noteText = (pk: number) => (notesFor.get(pk) ?? []).join('\n\n');

  // ---- People
  const people: Record<Id, Person> = {};
  const relationships: Record<Id, Relationship> = {};
  const personId = new Map<number, Id>();
  let homePersonId: Id | undefined;
  let hiddenReligious = 0;
  let preserved = 0;

  const persons = of('Person');
  for (const r of persons) {
    const pk = num(r.Z_PK)!;
    const uuid = str(r.ZUNIQUEID)?.replace(/-/g, '').toLowerCase();
    // Stable ids, so adding the same package to a tree again updates people instead of duplicating them.
    const id = uuid && /^[0-9a-f]{32}$/.test(uuid) ? `p${uuid}` : newId('p');
    personId.set(pk, id);
    const name: PersonName = {
      given: [str(r.ZFIRSTNAME), str(r.ZNAMEMIDDLE)].filter(Boolean).join(' '),
      surname: str(r.ZLASTNAME) ?? '',
      type: 'name',
    };
    if (str(r.ZNAMEPREFIX)) name.prefix = str(r.ZNAMEPREFIX);
    if (str(r.ZNAMESUFFIX)) name.suffix = str(r.ZNAMESUFFIX);
    const gedcomId = str(r.ZGEDCOMID);
    people[id] = {
      id,
      names: [name],
      gender: GENDERS[num(r.ZGENDER) ?? 2] ?? { identity: 'unknown' },
      living: null,
      events: [],
      notes: noteText(pk),
      tags: [],
      ...(gedcomId && /^[A-Za-z0-9_]+$/.test(gedcomId) ? { gedcomId: `@${gedcomId}@` } : {}),
    };
    if (r.ZISSTARTPERSON === 1 && !homePersonId) homePersonId = id;
  }

  // Additional names. A married name with only a surname keeps the person's given name.
  for (const r of db.rows('ZADDITIONALNAME')) {
    const p = people[personId.get(num(r.ZPERSON) ?? -1) ?? ''];
    if (!p) continue;
    const kind = NAME_TYPES[num(r.ZTYPE) ?? 2] ?? 'other';
    const given = [str(r.ZFIRSTNAME), str(r.ZNAMEMIDDLE)].filter(Boolean).join(' ');
    const surname = str(r.ZLASTNAME) ?? '';
    if (kind === 'nickname' && !surname && !p.names[0].nickname) {
      p.names[0].nickname = given;
      continue;
    }
    if (!given && !surname) continue;
    const n: PersonName = { given: given || p.names[0].given, surname, type: kind === 'nickname' ? 'alias' : kind };
    if (str(r.ZNAMEPREFIX)) n.prefix = str(r.ZNAMEPREFIX);
    if (str(r.ZNAMESUFFIX)) n.suffix = str(r.ZNAMESUFFIX);
    p.names.push(n);
  }

  // Labels are MacFamilyTree's tags.
  const labels = new Map<number, string>();
  for (const r of db.rows('ZLABEL')) if (str(r.ZTITLE)) labels.set(num(r.Z_PK)!, str(r.ZTITLE)!);
  for (const r of db.rows('ZLABELRELATION')) {
    const p = people[personId.get(num(r.ZBASEOBJECT) ?? -1) ?? ''];
    const label = labels.get(num(r.ZLABEL) ?? -1);
    if (p && label && !p.tags.includes(label)) p.tags.push(label);
  }

  // ---- Person events and facts
  const eventByPk = new Map<number, { personId: Id; eventId: Id }>();
  const dateAndPlace = (r: SqlRow): RawRecord[] => {
    const out: RawRecord[] = [];
    const date = readDate(str(pick(r, 'ZDATE')));
    const place = places.get(num(r.ZASSIGNEDPLACE) ?? -1);
    if (date) out.push(rec('DATE', date));
    if (place) out.push(rec('PLAC', place));
    return out;
  };
  for (const r of [...of('PersonEvent'), ...of('PersonFact')]) {
    const p = people[personId.get(num(pick(r, 'ZPERSON')) ?? -1) ?? ''];
    if (!p) continue;
    const ct = conclusionTypes.get(num(pick(r, 'ZCONCLUSIONTYPE')) ?? -1) ?? { tag: 'EVEN', label: 'Event' };
    const isFact = isA(r.Z_ENT, 'PersonFact');
    const detail = str(isFact ? r.ZVALUE : r.ZUSERDESCRIPTION) ?? str(r.ZUSERDESCRIPTION);
    const pk = num(r.Z_PK)!;
    if (RELIGIOUS_TAGS.has(ct.tag) || MFT_RELIGIOUS_TAGS.has(ct.tag) || PRIVATE_FACT_TAGS.has(ct.tag)) {
      (p.passthrough ??= []).push(rec(ct.tag, detail, dateAndPlace(r)));
      if (PRIVATE_FACT_TAGS.has(ct.tag)) preserved++;
      else hiddenReligious++;
      continue;
    }
    if (ct.tag === 'NICK' && detail) {
      if (!p.names[0].nickname) p.names[0].nickname = detail;
      else p.names.push({ given: detail, surname: '', type: 'alias' });
      continue;
    }
    const type: EventType = (!isFact && (EVENT_TAGS[ct.tag] ?? MFT_EVENT_TAGS[ct.tag])) || 'custom';
    const ev: LifeEvent = { id: newId('e'), type };
    eventByPk.set(pk, { personId: p.id, eventId: ev.id });
    // Only some event types show a label (eventTitle); elsewhere the description goes in the note.
    const labeled = type === 'custom' || type === 'occupation' || type === 'education';
    if (type === 'custom') ev.label = detail ? `${ct.label}: ${detail}` : ct.label;
    else if (detail && labeled) ev.label = detail;
    const date = readDate(str(pick(r, 'ZDATE')));
    if (date) ev.date = date;
    const place = places.get(num(r.ZASSIGNEDPLACE) ?? -1);
    if (place) ev.place = place;
    const notes = [labeled ? '' : (detail ?? ''), noteText(pk)];
    if (str(r.ZCAUSE)) notes.push(`Cause: ${str(r.ZCAUSE)}`);
    if (str(pick(r, 'ZADDRESS'))) notes.push(`Address: ${str(pick(r, 'ZADDRESS'))}`);
    if (str(pick(r, 'ZAGENCY'))) notes.push(`Agency: ${str(pick(r, 'ZAGENCY'))}`);
    const note = notes.filter(Boolean).join('\n');
    if (note) ev.note = note;
    p.events.push(ev);
  }

  // ---- Families → partner links and parent links
  const familyEvents = new Map<number, SqlRow[]>();
  const familyEventFamily = new Map<number, number>();
  for (const r of of('FamilyEvent')) {
    const fam = num(pick(r, 'ZFAMILY'));
    if (fam !== undefined) familyEvents.set(fam, [...(familyEvents.get(fam) ?? []), r]);
  }
  const childrenOf = new Map<number, SqlRow[]>();
  for (const r of db.rows('ZCHILDRELATION')) {
    const fam = num(r.ZFAMILY);
    if (fam !== undefined) childrenOf.set(fam, [...(childrenOf.get(fam) ?? []), r]);
  }
  for (const list of childrenOf.values()) list.sort((a, b) => (num(a.ZCUSTOMORDER) ?? 0) - (num(b.ZCUSTOMORDER) ?? 0));

  let partnerships = 0;
  let parentLinks = 0;
  const familyPartners = new Map<number, Id[]>();
  const familyRel = new Map<number, PartnerRel>();
  const unlabeledCouples: Id[] = [];
  for (const f of of('Family')) {
    const pk = num(f.Z_PK)!;
    const man = personId.get(num(f.ZMAN) ?? -1);
    const woman = personId.get(num(f.ZWOMAN) ?? -1);
    familyPartners.set(pk, [man, woman].filter((x): x is Id => !!x));
    const events = (familyEvents.get(pk) ?? []).map((r) => ({ r, tag: conclusionTypes.get(num(pick(r, 'ZCONCLUSIONTYPE')) ?? -1)?.tag ?? 'EVEN' }));
    const first = (tags: string[]) => tags.map((t) => events.find((e) => e.tag === t)).find(Boolean);

    if (man && woman && man !== woman) {
      const marr = first(MARRIAGES) ?? first(MARRIAGE_PAPERWORK);
      const div = first(DIVORCES);
      const enga = first(['ENGA']);
      const sep = first(['_SEP']);
      const prs = first(['_PRS']);
      // As with GEDCOM, a couple with no marriage or divorce on record are partners, not spouses,
      // and the import window asks whether that's right.
      const type: PartnerRel['type'] = marr || div ? 'married' : enga ? 'engaged' : 'partner';
      const unlabeled = !marr && !div && !enga && !prs;
      const rel: PartnerRel = { id: newId('r'), kind: 'partner', a: man, b: woman, type, status: div ? 'divorced' : sep ? 'separated' : 'current' };
      const startEv = marr ?? (type === 'engaged' ? enga : prs);
      const start = startEv && readDate(str(pick(startEv.r, 'ZDATE')));
      if (start) rel.start = start;
      const end = (div ?? sep) && readDate(str(pick((div ?? sep)!.r, 'ZDATE')));
      if (end) rel.end = end;
      const place = startEv && places.get(num(startEv.r.ZASSIGNEDPLACE) ?? -1);
      if (place) rel.place = place;
      const notes = [noteText(pk), ...events.map((e) => noteText(num(e.r.Z_PK)!))].filter(Boolean);
      if (notes.length) rel.notes = notes.join('\n\n');
      // Other family events (census, banns after a wedding, sealings…) are kept for GEDCOM export.
      const used = new Set([marr, div, type === 'engaged' ? enga : undefined, sep, prs]);
      const extra = events.filter((e) => !used.has(e)).map((e) => rec(e.tag, undefined, dateAndPlace(e.r)));
      for (const e of extra) if (RELIGIOUS_TAGS.has(e.tag)) hiddenReligious++;
      if (extra.length) rel.passthrough = extra;
      relationships[rel.id] = rel;
      familyRel.set(pk, rel);
      if (unlabeled) unlabeledCouples.push(rel.id);
      for (const e of events) familyEventFamily.set(num(e.r.Z_PK)!, pk);
      partnerships++;
    }

    const kids = (childrenOf.get(pk) ?? []).map((c) => ({ c, id: personId.get(num(c.ZCHILD) ?? -1) })).filter((k): k is { c: SqlRow; id: Id } => !!k.id);
    for (const { c, id } of kids) {
      for (const [parent, col] of [[man, 'ZRELATIONTYPETOMANINFAMILY'], [woman, 'ZRELATIONTYPETOWOMANINFAMILY']] as const) {
        if (!parent || parent === id) continue;
        const rel: Relationship = { id: newId('r'), kind: 'parent', a: parent, b: id, type: CHILD_TYPES[num(c[col]) ?? 0] ?? 'unknown' };
        relationships[rel.id] = rel;
        parentLinks++;
      }
    }
    if (!man && !woman && kids.length > 1) {
      for (let i = 0; i < kids.length; i++)
        for (let j = i + 1; j < kids.length; j++) {
          const rel: Relationship = { id: newId('r'), kind: 'sibling', a: kids[i].id, b: kids[j].id, type: 'unknown' };
          relationships[rel.id] = rel;
        }
    }
  }

  // ---- Associates: godparents and witnesses, attached to a person, a family, or an event.
  let socialLinks = 0;
  const seen = new Set<string>();
  for (const r of db.rows('ZASSOCIATERELATION')) {
    const to = personId.get(num(r.ZASSOCIATEDPERSON) ?? -1);
    const owner = byPk.get(num(r.ZASSOCIATEDCONTAINER) ?? -1);
    if (!to || !owner) continue;
    let from: Id[] = [];
    if (isA(owner.Z_ENT, 'Person')) from = [personId.get(num(owner.Z_PK)!)].filter((x): x is Id => !!x);
    else if (isA(owner.Z_ENT, 'PersonEvent')) from = [personId.get(num(pick(owner, 'ZPERSON')) ?? -1)].filter((x): x is Id => !!x);
    else if (isA(owner.Z_ENT, 'Family')) from = familyPartners.get(num(owner.Z_PK)!) ?? [];
    else if (isA(owner.Z_ENT, 'FamilyEvent')) from = familyPartners.get(num(pick(owner, 'ZFAMILY')) ?? -1) ?? [];
    const label = ASSOCIATE_LABELS[num(r.ZTYPE) ?? -1] ?? 'Associate';
    for (const a of from) {
      const key = `${a}|${to}|${label}`;
      if (a === to || seen.has(key)) continue;
      seen.add(key);
      const start = readDate(str(r.ZDATE));
      const rel: Relationship = { id: newId('r'), kind: 'social', type: 'other', a, b: to, label, ...(start ? { start } : {}) };
      relationships[rel.id] = rel;
      socialLinks++;
    }
  }

  // What an object pk refers to on our side, for citations, media, and to-dos.
  const peopleFor = (pk: number): Id[] => {
    const row = byPk.get(pk);
    if (!row) return [];
    if (isA(row.Z_ENT, 'Person')) return [personId.get(pk)].filter((x): x is Id => !!x);
    if (isA(row.Z_ENT, 'PersonEvent') || isA(row.Z_ENT, 'PersonFact')) return [personId.get(num(pick(row, 'ZPERSON')) ?? -1)].filter((x): x is Id => !!x);
    if (isA(row.Z_ENT, 'Family')) return familyPartners.get(pk) ?? [];
    if (isA(row.Z_ENT, 'FamilyEvent')) return familyPartners.get(num(pick(row, 'ZFAMILY')) ?? -1) ?? [];
    return [];
  };

  // ---- Files, read once each.
  const files: FileBag = new Map();
  const keyOfPath = new Map<string, { key: string; type: string; size: number } | null>();
  const load = async (path: string | undefined) => {
    if (!path) return null;
    if (keyOfPath.has(path)) return keyOfPath.get(path)!;
    const bytes = await pkg.read(`resources/${path}`);
    let out: { key: string; type: string; size: number } | null = null;
    if (bytes?.length) {
      const type = mimeFromName(path);
      const key = await fileKey(bytes);
      files.set(key, new Blob([bytes], { type }));
      out = { key, type, size: bytes.length };
    }
    keyOfPath.set(path, out);
    return out;
  };

  // ---- Sources
  const sources: Record<Id, Source> = {};
  const sourceId = new Map<number, Id>();
  for (const r of of('Source')) {
    const pk = num(r.Z_PK)!;
    const uuid = str(r.ZUNIQUEID)?.replace(/-/g, '').toLowerCase();
    const id = uuid && /^[0-9a-f]{32}$/.test(uuid) ? `s${uuid}` : newId('s');
    sourceId.set(pk, id);
    const repo = byPk.get(num(r.ZSOURCEREPOSITORY) ?? -1);
    const notes = [noteText(pk), str(r.ZAGENCY) && `Agency: ${str(r.ZAGENCY)}`, str(r.ZABBREVIATION) && `Abbreviation: ${str(r.ZABBREVIATION)}`];
    const repoAddress = repo && str(pick(repo, 'ZADDRESS'));
    if (repoAddress) notes.push(`Repository address: ${repoAddress}`);
    const src: Source = { id, title: str(r.ZTITLE) ?? str(r.ZPUBLICATION) ?? 'Untitled source' };
    const set = <K extends keyof Source>(k: K, v: Source[K] | undefined) => {
      if (v) src[k] = v;
    };
    set('author', str(r.ZAUTHOR));
    set('publication', str(r.ZPUBLICATION) !== src.title ? str(r.ZPUBLICATION) : undefined);
    set('date', readDate(str(r.ZDATE)));
    set('place', str(r.ZPLACE));
    set('kind', str(r.ZREFERENCETYPE));
    set('callNumber', str(r.ZREFERENCENUMBER));
    set('repository', repo ? str(pick(repo, 'ZNAME')) : undefined);
    set('text', str(pick(r, 'ZTEXT')));
    set('notes', notes.filter(Boolean).join('\n\n') || undefined);
    const gid = str(r.ZGEDCOMID);
    if (gid && /^[A-Za-z0-9_]+$/.test(gid)) src.gedcomId = `@${gid}@`;
    sources[id] = src;
  }

  // ---- Citations ("source relations") on people, events, and families.
  let citations = 0;
  for (const r of of('SourceRelation')) {
    const src = sourceId.get(num(r.ZSOURCE) ?? -1);
    const owner = num(r.ZSOURCECONTAINER);
    if (!src || owner === undefined) continue;
    const c: Citation = { sourceId: src };
    if (str(r.ZPAGE)) c.page = str(r.ZPAGE);
    // Information: don't know, secondary, primary. Evidence: don't know, negative, indirect, direct.
    const info = num(r.ZQUALITYINFORMATION) ?? 0;
    const evidence = num(r.ZQUALITYEVIDENCE) ?? 0;
    const quality: CitationQuality | undefined = info === 2 ? (evidence === 1 ? 1 : 3) : info === 1 ? 2 : evidence === 1 ? 1 : undefined;
    if (quality !== undefined) c.quality = quality;
    const original = num(r.ZQUALITYSOURCE);
    const note = [noteText(num(r.Z_PK)!), original === 2 ? 'Original source' : original === 1 ? 'Derivative source' : ''].filter(Boolean).join('\n');
    if (note) c.note = note;
    const row = byPk.get(owner);
    if (!row) continue;
    const ev = eventByPk.get(owner);
    const fam = isA(row.Z_ENT, 'Family') ? owner : familyEventFamily.get(owner);
    if (ev) {
      const e = people[ev.personId].events.find((x) => x.id === ev.eventId)!;
      (e.citations ??= []).push(c);
    } else if (isA(row.Z_ENT, 'Person') && personId.has(owner)) {
      (people[personId.get(owner)!].citations ??= []).push(c);
    } else if (fam !== undefined && familyRel.has(fam)) {
      (familyRel.get(fam)!.citations ??= []).push(c);
    } else continue;
    citations++;
  }

  // ---- Media: every picture, document, recording, video, and link, with who's in it.
  const media: Record<Id, MediaItem> = {};
  const mediaByPk = new Map<number, MediaItem>();
  const thumbs = new Map<Id, Blob>();
  const untitled = new Set<Id>();
  let missingFiles = 0;
  for (const [entity, kind, column] of MEDIA_KINDS) {
    for (const r of of(entity)) {
      const pk = num(r.Z_PK)!;
      const uuid = str(r.ZUNIQUEID)?.replace(/-/g, '').toLowerCase();
      const id = uuid && /^[0-9a-f]{32}$/.test(uuid) ? `m${uuid}` : newId('m');
      const location = str(pick(r, column)) ?? (kind === 'image' ? str(pick(r, 'ZTHUMBNAILFILEIDENTIFIER')) : undefined);
      const item: MediaItem = { id, title: str(pick(r, 'ZTITLE')) ?? '', kind, people: [] };
      if (kind === 'link') {
        if (location) item.url = location;
      } else {
        const f = await load(location);
        if (f) item.file = { ...f, name: location!.split('/').pop() };
        else {
          if (location) item.url = location;
          missingFiles++;
        }
        if (kind !== 'image') {
          const t = await load(str(pick(r, 'ZTHUMBNAILFILEIDENTIFIER')));
          if (t) thumbs.set(id, files.get(t.key)!);
        }
      }
      if (!item.title) {
        // Named after the people in it once they're known (below), else after the file.
        untitled.add(id);
        item.title = (kind === 'link' ? location : location?.split('/').pop()?.replace(/\.[^.]+$/, '')) || 'Untitled';
      }
      const description = noteText(pk);
      if (description) item.description = description;
      media[id] = item;
      mediaByPk.set(pk, item);
    }
  }
  // Face rectangles hang off the link between a picture and a person.
  const regions = new Map<number, Region>();
  for (const r of db.rows('ZBASEMEDIARELATIONASSIGNMENT')) {
    const region = mftRegion(str(r.ZRECTSTRING));
    if (region && num(r.ZMEDIARELATION) !== undefined) regions.set(num(r.ZMEDIARELATION)!, region);
  }
  const firstPicture = new Map<Id, { order: number; item: MediaItem; region?: Region }>();
  for (const r of db.rows('ZMEDIARELATION')) {
    const item = mediaByPk.get(num(r.ZBASEMEDIA) ?? -1);
    const owner = num(r.ZMEDIACONTAINER);
    if (!item || owner === undefined) continue;
    const ownerRow = byPk.get(owner);
    if (ownerRow && isA(ownerRow.Z_ENT, 'Source')) {
      const sid = sourceId.get(owner);
      if (sid && !item.sourceIds?.includes(sid)) (item.sourceIds ??= []).push(sid);
      continue;
    }
    const region = regions.get(num(r.Z_PK)!);
    for (const id of peopleFor(owner)) {
      const existing = item.people.find((x) => x.personId === id);
      if (existing) {
        if (region && !existing.region) existing.region = region;
      } else item.people.push(region ? { personId: id, region } : { personId: id });
    }
    // A person's own picture (not a family's or an event's) can be their profile photo.
    const pid = ownerRow && isA(ownerRow.Z_ENT, 'Person') ? personId.get(owner) : undefined;
    const order = num(r.ZORDER) ?? 0;
    if (pid && item.kind === 'image' && item.file) {
      const cur = firstPicture.get(pid);
      if (!cur || order < cur.order) firstPicture.set(pid, { order, item, region });
    }
  }
  for (const id of untitled) {
    const item = media[id];
    const names = item.people.map((x) => people[x.personId]?.names[0]).filter(Boolean).map((n) => [n.given, n.surname].filter(Boolean).join(' ') || 'Unnamed');
    if (!names.length) continue;
    const who = names.length <= 3 ? names.join(names.length === 2 ? ' and ' : ', ').replace(/, ([^,]*)$/, ', and $1') : `${names.slice(0, 2).join(', ')}, and ${names.length - 2} others`;
    item.title = `${MEDIA_NOUN[item.kind]} of ${who}`;
  }
  const photos: PackagePhoto[] = [...firstPicture].map(([pid, { item, region }]) => ({ personId: pid, key: item.file!.key, ...(region ? { region } : {}) }));
  if (missingFiles) warnings.push(`${plural(missingFiles, 'media file')} listed in the database ${missingFiles === 1 ? "wasn't" : "weren't"} in the package.`);

  // ---- To-dos
  const todos: Record<Id, Todo> = {};
  const todoPeople = new Map<number, Id[]>();
  for (const r of db.rows('ZTODORELATION')) {
    const td = num(r.ZTODO);
    if (td !== undefined) todoPeople.set(td, [...new Set([...(todoPeople.get(td) ?? []), ...peopleFor(num(r.ZBASEOBJECT) ?? -1)])]);
  }
  const now = new Date().toISOString();
  for (const r of db.rows('ZTODO')) {
    const pk = num(r.Z_PK)!;
    const uuid = str(r.ZUNIQUEID)?.replace(/-/g, '').toLowerCase();
    const id = uuid && /^[0-9a-f]{32}$/.test(uuid) ? `td${uuid}` : newId('td');
    const status = TODO_STATUSES[num(r.ZSTATUS) ?? 0] ?? 'open';
    const td: Todo = {
      id,
      title: str(r.ZTITLE) ?? 'Untitled to-do',
      status,
      priority: TODO_PRIORITIES[num(r.ZPRIORITY) ?? 1] ?? 'normal',
      people: todoPeople.get(pk) ?? [],
      createdAt: now,
    };
    const kind = TODO_KINDS[num(r.ZTYPE) ?? -1];
    if (kind) td.kind = kind;
    const dueText = str(r.ZDUEDATE);
    const due = isoDay(dueText, dayFirst);
    if (due) td.due = due;
    const notes = [str(r.ZNOTE), dueText && !due ? `Due: ${dueText}` : ''].filter(Boolean).join('\n\n');
    if (notes) td.notes = notes;
    if (status === 'done') td.doneAt = now;
    todos[id] = td;
  }

  // Deceased when their records show it; MacFamilyTree has no living flag, so otherwise not recorded.
  markDeceasedFromDates(Object.values(people), Object.values(relationships));

  const notImported: string[] = [];
  const dna = of('DNATestResult').length;
  if (dna) notImported.push(plural(dna, 'DNA test result'));

  const tree: Tree = {
    ...newTree(pkg.name || 'MacFamilyTree import'),
    people,
    relationships,
    sources,
    media,
    todos,
    homePersonId: homePersonId ?? Object.keys(people)[0],
    locations: Object.keys(locations).length ? locations : undefined,
  };
  return {
    tree,
    photos,
    files,
    thumbs,
    summary: {
      people: persons.length,
      partnerships,
      parentLinks,
      socialLinks,
      groups: 0,
      preservedRecords: preserved,
      hiddenReligiousRecords: hiddenReligious,
      source: 'MacFamilyTree',
      photos: photos.length,
      sources: Object.keys(sources).length,
      citations,
      unlabeledCouples,
      media: Object.keys(media).length,
      todos: Object.keys(todos).length,
      notImported,
      warnings,
    },
  };
}

/** Degrees, minutes, and seconds (kept as text) as signed decimal degrees, to about 10 cm. */
function degrees(d: SqlValue, m: SqlValue, s: SqlValue, negative: SqlValue): number | undefined {
  const n = (v: SqlValue) => (typeof v === 'number' ? v : typeof v === 'string' && v.trim() && isFinite(Number(v)) ? Number(v) : undefined);
  const deg = n(d);
  if (deg === undefined) return undefined;
  const value = Math.abs(deg) + (n(m) ?? 0) / 60 + (n(s) ?? 0) / 3600;
  return Math.round((num(negative) ? -value : value) * 1e6) / 1e6;
}
